import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import UpdatedBillOfLadingAnimation from "@/Shared/Icons/UpdatedBillOfLadingAnimation";
import type { ReactNode } from "react";
import { useRef, useState } from "react";

interface ViewShipmentsUpdatedBOLDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// "Updated Shipment Documents" notice shown after any post-booking change
// that regenerates the bill of lading.
function ViewShipmentsUpdatedBOLDialog_(
  props: ViewShipmentsUpdatedBOLDialogProps,
) {
  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Updated Shipment Documents"
      width="narrow"
      footer={
        <Button primary onClick={() => props.onOpenChange(false)}>
          Done
        </Button>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        <Stack gap={4}>
          <T size="sm" color="secondary">
            Details of the shipment have been updated, and a new bill of lading
            has been generated, and emailed to the shipper.
          </T>
          <Row justify="center" grow>
            <UpdatedBillOfLadingAnimation />
          </Row>
          <T size="sm" color="secondary">
            To avoid delays in shipment, please make sure the new bill of lading
            is given to the driver for pickup
          </T>
        </Stack>
      </ControlPresentationProvider>
    </Dialog>
  );
}

// Awaitable wrapper: post-booking flows show the notice and continue once the
// user dismisses it.
export function useUpdatedBOLDialog(): {
  show: () => Promise<void>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef<(() => void) | undefined>(undefined);

  function show(): Promise<void> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpen(true);
    });
  }

  const dialogs = (
    <ViewShipmentsUpdatedBOLDialog_
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          resolverRef.current?.();
          resolverRef.current = undefined;
        }
      }}
    />
  );

  return { show, dialogs };
}
